import React, { useCallback } from 'react';
import Confirmation from 'pages/Confirmation';
import { ClinicWidgetRequestTypeResponse, ClinicWidgetSettingResponse } from 'shared/types';
import { ProfileErrorMessage, ProfileErrorTitle, useWidgetStore } from 'state/useWidgetStore';
import Authentication from 'pages/Authentication';
import RequestForm from 'pages/RequestForm';
import { WidgetRequestType } from 'shared/types/graphql';
import PrescriptionRefill from 'pages/PrescriptionRefill';
import DirectBooking from 'pages/DirectBooking';
import OneTimePassword from 'pages/OneTimePassword';
import Teletriage from 'pages/Teletriage';
import { FormContainer } from 'shared/components/FormContainer';
import useAuth from 'shared/hooks/useAuth';
import RequestTypeList from './RequestTypeList';
import RequestTypeListContainer from '../ClinicSiteWidget/RequestTypeListContainer';
import { Box } from '@televet/kibble-ui/build/chakra';
import { Alert } from '@televet/kibble-ui/build/components/Alert';

interface WidgetBodyProps {
  requestTypes: ClinicWidgetRequestTypeResponse[] | null;
  widgetSetting: ClinicWidgetSettingResponse;
}

const WidgetBody = ({ requestTypes, widgetSetting }: WidgetBodyProps): JSX.Element | null => {
  const {
    requestSteps,
    currentStepIndex,
    selectedRequest,
    isPetPortal,
    setSelectedRequest,
    petParentId,
    goToNextStep,
    authentication: { hasNoPets, showAuthenticationAlert },
  } = useWidgetStore((state) => state);
  const { lookUpPetParent } = useAuth();

  const handleRequestTypeClick = useCallback(
    async (request: ClinicWidgetRequestTypeResponse): Promise<void> => {
      setSelectedRequest(request);

      if (!isPetPortal) {
        goToNextStep();
      } else {
        await lookUpPetParent({ petParentId, requestType: request?.requestType });
      }
    },
    [goToNextStep, isPetPortal, lookUpPetParent, petParentId, setSelectedRequest],
  );

  // Shared request steps agnostic to request type
  switch (requestSteps[currentStepIndex]) {
    case 'ContactInfo':
      return (
        <FormContainer>
          <Authentication />
        </FormContainer>
      );
    case 'RequestForm':
      return (
        <FormContainer>
          <RequestForm />
        </FormContainer>
      );
    case 'OneTimeCode':
      return <OneTimePassword />;
    case 'Confirmation':
      return <Confirmation />;
  }

  // Request type specific steps
  switch (selectedRequest?.requestType) {
    case WidgetRequestType.AdvancedRequestRxRefill:
      return (
        <FormContainer>
          <PrescriptionRefill />
        </FormContainer>
      );
    case WidgetRequestType.DirectBooking:
      return (
        <FormContainer>
          <DirectBooking widgetSetting={widgetSetting} />
        </FormContainer>
      );
    case WidgetRequestType.Teletriage:
      return (
        <FormContainer>
          <Teletriage />
        </FormContainer>
      );
  }

  return isPetPortal ? (
    <>
      {showAuthenticationAlert && (
        <Box px={3} pt={3}>
          <Alert
            hideCloseButton
            status="warning"
            title={hasNoPets ? ProfileErrorTitle.HasNoPets : ProfileErrorTitle.HasNoProfile}
            description={hasNoPets ? ProfileErrorMessage.HasNoPets : ProfileErrorMessage.HasNoProfile}
          />
        </Box>
      )}
      <RequestTypeList requestTypes={requestTypes} handleRequestTypeClick={handleRequestTypeClick} />
    </>
  ) : (
    <RequestTypeListContainer>
      <RequestTypeList requestTypes={requestTypes} handleRequestTypeClick={handleRequestTypeClick} />
    </RequestTypeListContainer>
  );
};

export default WidgetBody;
